import { LuLoaderCircle } from "react-icons/lu";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { SubscriptionProduct } from "../../../model/subscriptionProduct";

export default function SubscriptionsModalItem({
  product,
  isDisabled,
  isLoading,
  onSelect,
}: {
  product: SubscriptionProduct;
  isDisabled: boolean;
  isLoading: boolean;
  onSelect: () => void;
}) {
  const isCompactView = useIsCompactView();
  const isFreePlan = product.price === 0;
  const isActive = product.status === "active";

  // Compact static view for active free plan on mobile
  if (isCompactView && isActive && isFreePlan) {
    return (
      <div
        className="w-full px-4 py-3
          bg-dark-surface-secondary
          rounded-xl
          border border-dark-border
          text-dark-text"
      >
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold">{product.name}</span>
          <span className="text-sm font-medium text-dark-text-secondary">Active</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-sm font-semibold text-dark-text">$0</span>
          <span className="text-sm text-dark-text-secondary">
            {product.marketing_features?.[0]}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      key={product.name}
      className={`h-full ${isCompactView && "min-h-[300px]"}
        relative
        bg-dark-surface-secondary
        flex flex-col
        rounded-xl
        border border-dark-border
        ${!isCompactView && "hover:cursor-pointer hover:-translate-y-1 hover:scale-[1.02] group"}
        transition-all duration-300
        shadow-sm`}
    >
      {/* Colorful gradient glow on hover only (desktop) */}
      {!isCompactView && (
        <div
          className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-0"
          style={{
            boxShadow: '0 0 20px rgba(233, 30, 99, 0.5), 0 0 40px rgba(156, 39, 176, 0.4)',
          }}
        />
      )}

      <div
        className="w-full h-full px-4 py-4
        flex flex-col items-center justify-between
      text-dark-text relative z-10"
      >
        <Description
          planName={product.name}
          tokens={product.marketing_features?.join(", ")}
          description={product.description}
        />
        <Price price={product.price} currency={product.currency} />
        <BottomPart
          buttonId={`subscriptions-modal-item-${product.name
            .toLowerCase()
            .replace(/ /g, "-")}`}
          status={product.status}
          onClick={onSelect}
          isDisabled={isDisabled}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function Description({
  planName,
  tokens,
  description,
}: {
  planName: string;
  tokens: string;
  description: string;
}) {
  return (
    <div
      className="flex-1 flex flex-col gap-3
    items-center text-center py-2"
    >
      <div className="text-2xl font-semibold">{planName}</div>
      <div className="text-sm">{tokens}</div>
      <div
        className={`text-sm text-dark-placeholder`}
      >
        {description}
      </div>
    </div>
  );
}

function Price({ price, currency }: { price: number; currency: string }) {
  const formatCurrency = (amount: number, currencyCode: string) => {
    try {
      // Convert amount from cents to dollars (divide by 100)
      const amountInCurrency = amount / 100;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amountInCurrency);
    } catch (error) {
      return `${currencyCode.toUpperCase()}${amount / 100}`;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-3">
      <div className={`text-4xl font-bold`}>
        {formatCurrency(price, currency)}
        {price > 0 && <span className="text-lg">/month</span>}
      </div>
    </div>
  );
}

function BottomPart({
  buttonId,
  status,
  isDisabled,
  isLoading,
  onClick,
}: {
  buttonId: string;
  status: string;
  isDisabled: boolean;
  isLoading: boolean;
  onClick: () => void;
}) {
  return (
    <div className="w-full flex items-end justify-center pt-3">
      {status === "active" ? (
        <div
          className="h-[48px] font-semibold text-dark-text-secondary
        flex items-center justify-center"
        >
          Active
        </div>
      ) : (
        <button
          id={buttonId}
          className="bg-primary-blue text-white w-full h-[48px] max-w-[240px]
            rounded-full font-semibold text-base
            transition-opacity duration-300
            hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed
            flex items-center justify-center gap-3"
          onClick={onClick}
          disabled={isDisabled}
        >
          {isLoading ? (
            <LuLoaderCircle className="animate-spin" size={20} />
          ) : (
            status[0].toUpperCase() + status.slice(1)
          )}
        </button>
      )}
    </div>
  );
}
